import React from 'react'
import PropTypes from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import { Wrapper } from '@farewill/ui'
import { COLOR } from '@farewill/ui/tokens'
import { FUNERAL } from 'lib/products/constants'
import { CTA_TYPES } from 'components/Cta/constants'
import { normalizeArray } from 'lib/graphql/normalize'
import { formatPriceInPounds } from 'lib/formatting/pricing'
import { CREMATION_SIMPLE_FEE } from 'config'
import GuideCards from 'components/GuideCards'
import { TRUSTMARKS } from 'components/AccoladesProduct/constants'
import WrapperWithEdge from 'components/WrapperWithEdge'
import FuneralDirectors from '../../../cremation/components/FuneralDirectors'
import Tips from './components/Tips'
import Footer from '../components/Footer'
import Quote from '../../../cremation/components/QuoteLocal'
import WhatWeOffer from './components/WhatWeOffer'
import { CUSTOMER_QUOTE_TYPES } from './constants'
import FreetextContent from './components/FreetextContent'
import SliceCustomerQuote from './components/SliceCustomerQuote'
import SliceHero from './components/SliceHero'
import SliceFeatures from './components/SliceFeatures'
import SliceCrematoriaMap from './components/SliceCrematoriaMap'
import { getCrematoria } from './utils'

const whatToDoGuideCard = {
  title: 'What to do when someone dies',
  image: {
    file: {
      url: 'https://images.ctfassets.net/5bw65n0fz39f/28z5gpodTHkkjwZ90rk8oL/e2544562beb15ddc3719d58c8183c024/Humanist_Ceremony.png',
    },
  },
  slug: 'what-to-do-when-someone-dies',
}

const formatName = (name) => {
  const wordToExtract = 'the '
  return name?.toLowerCase().startsWith(wordToExtract)
    ? name.slice(wordToExtract.length)
    : name
}

const startsWithVowel = (name) => {
  const vowelFirstRegEx = /^[aeiouAIEOU]/
  return name?.match(vowelFirstRegEx)
}

const LocalFuneralPageContent = ({ data, allCrematorium, isPpcPage }) => {
  // Ignoring, because we'll use that data for other components
  // eslint-disable-next-line no-unused-vars
  const {
    nameShort,
    nameLong,
    phoneNumber,
    tips,
    freetextContent,
    crematoriaList,
  } = data

  const crematoria = getCrematoria(crematoriaList, allCrematorium)

  const { guideData } = useStaticQuery(graphql`
    query {
      guideData: allContentfulGuide(
        filter: { slug: { ne: "how-to-make-a-will" } }
        sort: { fields: updatedAt, order: DESC }
        limit: 3
      ) {
        edges {
          node {
            ...GuideCardFields
          }
        }
      }
    }
  `)

  const guides = [whatToDoGuideCard, ...normalizeArray(guideData)]

  const formattedName = formatName(nameShort)
  const headerStart = startsWithVowel(formattedName) ? 'An' : 'A'

  return (
    <Wrapper background={COLOR.WHITE}>
      <SliceHero
        ctaText="Get an instant quote"
        title={`Say a fitting farewell in ${nameShort}`}
        subtitle={`Arrange a local direct cremation from ${formatPriceInPounds(
          CREMATION_SIMPLE_FEE
        )}, with the help of our award-winning team. So you can say goodbye at a time and place that feel right.`}
        phoneNumber={phoneNumber}
      />
      <SliceCustomerQuote />
      <Wrapper container>
        <SliceFeatures nameShort={nameShort} />
      </Wrapper>
      <FuneralDirectors
        title={`${headerStart} ${formattedName} Funeral Specialist who’s with you from day one`}
        description={[
          "Losing a loved one is hard. To make things a little easier, we'll pair you up with someone in our team.",
          `With experience across ${nameLong} and beyond, they’ll listen to what you want and need, answer your questions and guide you through everything.`,
        ]}
        image="funerals/discovery/deeanna-funeral-specialist"
        caption="Deeanna, a Funeral Specialist at Farewill Funeral Directors"
        phoneNumber={phoneNumber}
      />

      <WhatWeOffer nameShort={nameShort} phoneNumber={phoneNumber} />

      {crematoria?.length > 0 && (
        <WrapperWithEdge
          direction="up"
          flipEdge
          lightAccent
          thickEdge
          padding={['XL', '0', '0']}
          paddingFromM={['XL', '0']}
          paddingFromL={['XL', 'S']}
        >
          <SliceCrematoriaMap
            nameShort={nameShort}
            phoneNumber={phoneNumber}
            crematoria={crematoria}
          />
        </WrapperWithEdge>
      )}

      <Quote
        type={
          isPpcPage ? CUSTOMER_QUOTE_TYPES.PPC : CUSTOMER_QUOTE_TYPES.ORGANIC
        }
        author={
          isPpcPage
            ? 'Stephen, who arranged a funeral with us'
            : 'Jenny, who arranged a funeral with farewill'
        }
        quote={
          isPpcPage
            ? `From the first telephone call to the delivery of the ashes, everything was handled professionally and with dignity; I couldn’t have been more satisfied and unconditionally recommend their service.`
            : `We met up with family and said goodbye together in our way. The way
          mum wanted. Fantastic value, fantastic staff, fantastic company that’s
          with you every step of the way.`
        }
        title={isPpcPage ? 'We’re here for you every step of the way' : null}
        image={
          isPpcPage
            ? 'local-funerals-crematoriums/mark-funeral-conductor'
            : 'local-funerals-crematoriums/customer-quote'
        }
        caption={
          isPpcPage
            ? 'Mark, a Funeral Conductor at Farewill Funeral Directors'
            : null
        }
      />

      {!isPpcPage && tips?.length > 0 && <Tips tipsList={tips} />}

      {freetextContent?.raw && <FreetextContent data={freetextContent} />}

      <Wrapper background={COLOR.BACKGROUND.FOG} paddingFromM={['M', 0, 0]}>
        <Wrapper
          container
          containerPaddingTop="XL"
          containerPaddingBottom="XXL"
        >
          <GuideCards title="Read our guides" guides={guides} />
        </Wrapper>
      </Wrapper>

      <Footer
        title="Create a meaningful funeral today"
        description="We’ve helped thousands of families deal with life’s toughest moments, and we’re here to support you whenever you need."
        cta={{
          type: CTA_TYPES.TELEPHONE_NUMBER,
          text: 'Call us now',
          phoneNumber,
        }}
        trustmarks={TRUSTMARKS.FUNERALS}
        background={COLOR.BACKGROUND.FOG}
        product={FUNERAL}
      />
    </Wrapper>
  )
}

LocalFuneralPageContent.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    pageType: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
    metaTitle: PropTypes.string.isRequired,
    metaDescription: PropTypes.shape({
      metaDescription: PropTypes.string,
    }),
    nameLong: PropTypes.string.isRequired,
    nameShort: PropTypes.string.isRequired,
    headerDescription: PropTypes.shape({
      headerDescription: PropTypes.string,
    }),
    crematoriumsReceptionVenues: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        longitude: PropTypes.number,
        latitude: PropTypes.number,
        image: PropTypes.string,
        id: PropTypes.string.isRequired,
        address: PropTypes.string,
        backstageId: PropTypes.string,
        description: PropTypes.shape({
          description: PropTypes.string,
        }).isRequired,
      })
    ),
    crematoriaList: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        externalId: PropTypes.string.isRequired,
      })
    ),
    tips: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        image: PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
        teamMember: PropTypes.shape({
          title: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          image: PropTypes.shape({
            file: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
        }),
        description: PropTypes.shape({
          description: PropTypes.string,
        }),
        id: PropTypes.string,
      })
    ),
    freetextContent: PropTypes.shape({
      raw: PropTypes.string,
    }),
  }).isRequired,
  isPpcPage: PropTypes.bool,
  allCrematorium: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          longitude: PropTypes.number,
          latitude: PropTypes.number,
          id: PropTypes.string,
          externalId: PropTypes.string,
          name: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
}

LocalFuneralPageContent.defaultProps = {
  isPpcPage: false,
}

export default LocalFuneralPageContent
